import { Service, service, inject } from './di'
import { PostcardService } from './PostcardService'
import { ConfigService } from './ConfigService'
import { LocationService } from './LocationService'
import { AuthProviderUser } from './definitions/IAuthenticationService'
import {
    AuthProvider,
    AccountsUser,
    LoyaltyCard,
    LoyaltyCardJSON,
    RatingObject,
} from '../definitions/CommonDefinitions'
import { fetchRaw, fetchJSON, FetchError } from '../utils/fetch'
import * as i18n from '../i18n/I18n'
import * as array from '../utils/array'

const CLIENT_ID = 'client-flipp-web'

interface LoyaltyCardOperationJSON {
    id: string
    client_id: string
    commit_version: string
    name: string
    loyalty_program_id: number
    card_id: string
    phone_number: string
    created_at: string
    updated_at: string
}

interface PostLoyaltyCardJSON {
    created: LoyaltyCardOperationJSON[]
    failed: LoyaltyCardOperationJSON[]
}

interface DeleteLoyaltyCardJSON {
    deleted: LoyaltyCardOperationJSON[]
    failed: LoyaltyCardOperationJSON[]
}

@service
export class AccountsService extends Service {
    @inject postcardService: PostcardService
    @inject locationService: LocationService
    @inject config: ConfigService

    fetchToken(user: AuthProviderUser) {
        const body: { [key: string]: string } = {
            platform: 'web',
        }
        switch (user.provider) {
            case AuthProvider.GOOGLE:
                body['google_id_token'] = user.accessToken
                break
            case AuthProvider.FACEBOOK:
                body['facebook_access_token'] = user.accessToken
                break
        }
        return fetchJSON<AccountsUser>(`${this.config.ACCOUNTS_SERVER}/token`, {
            method: 'POST',
            headers: [['Content-Type', 'application/json']],
            body: JSON.stringify(body),
        })
    }

    revokeToken(token: AccountsUser) {
        const body: { [key: string]: string } = {
            platform: 'web',
        }

        return fetchJSON<AccountsUser>(
            `${this.config.ACCOUNTS_SERVER}/users/${token.user_id}/revoke_token`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    getUser(token: AccountsUser) {
        return fetchJSON<AccountsUser>(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}`,
            {
                method: 'GET',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
            }
        )
    }

    updateSubscription(token: AccountsUser, subscribed: boolean) {
        const body: { [key: string]: string } = {
            platform: 'web',
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}?email_subscribed=${subscribed}`,
            {
                method: 'PUT',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    getLoyaltyCards(token: AccountsUser) {
        return fetchJSON<LoyaltyCardJSON>(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards`,
            {
                method: 'GET',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
            }
        )
    }

    postLoyaltyCards(token: AccountsUser, card: LoyaltyCard) {
        return this.postcardService
            .registerLoyaltyCard(token.user_id, card)
            .then(() => {
                const serializedCards = [card].map((card) => ({
                    client_id: CLIENT_ID,
                    card_id: card.cardId,
                    loyalty_program_id: card.loyaltyProgramId,
                    name: card.name,
                    phone_number: card.phoneNumber,
                }))
                return fetchJSON<PostLoyaltyCardJSON>(
                    `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards/batch_create`,
                    {
                        method: 'POST',
                        headers: [
                            ['Content-Type', 'application/json'],
                            ['Accept', 'application/json'],
                            [
                                'Authorization',
                                `Token token=${token.access_token}`,
                            ],
                        ],
                        body: JSON.stringify({
                            loyalty_cards: serializedCards,
                        }),
                    }
                ).then((resp: PostLoyaltyCardJSON) => {
                    const success = array.find(
                        resp.created,
                        (createdCard: LoyaltyCardOperationJSON) => {
                            if (card.phoneNumber) {
                                return (
                                    card.phoneNumber ===
                                    createdCard.phone_number
                                )
                            } else if (card.cardId) {
                                return card.cardId === createdCard.card_id
                            } else {
                                return false
                            }
                        }
                    )

                    if (!success) {
                        throw new Error(i18n.i18n('add_card_error'))
                    }
                })
            })
            .catch((err: Error) => {
                if (err instanceof FetchError) {
                    const body = JSON.parse(err.body)
                    throw new Error(
                        this.postcardService.getErrorMessage(body.error_code)
                    )
                } else {
                    throw new Error(i18n.i18n('add_card_error'))
                }
            })
    }

    deleteLoyaltyCards(token: AccountsUser, card: LoyaltyCard) {
        return this.postcardService
            .unregisterLoyaltyCard(token.user_id, card)
            .then(() => {
                const serializedCards = [card].map((card) => ({
                    client_id: CLIENT_ID,
                    id: card.id,
                    last_commit_version: card.commitVersion,
                }))
                return fetchJSON<DeleteLoyaltyCardJSON>(
                    `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards/batch_delete`,
                    {
                        method: 'POST',
                        headers: [
                            ['Content-Type', 'application/json'],
                            ['Accept', 'application/json'],
                            [
                                'Authorization',
                                `Token token=${token.access_token}`,
                            ],
                        ],
                        body: JSON.stringify({
                            loyalty_cards: serializedCards,
                        }),
                    }
                ).then((resp: DeleteLoyaltyCardJSON) => {
                    const success = array.find(
                        resp.deleted,
                        (entry: LoyaltyCardOperationJSON) =>
                            entry.id === card.id
                    )

                    if (!success) {
                        throw new Error(i18n.i18n('remove_card_error'))
                    }
                })
            })
    }

    deleteUserData(data: any) {
        const body: { [key: string]: string } = {
            email: data.email,
            sid: data.sid,
            fsa: data.fsa,
            locale: data.locale,
            platform: 'flipp_web',
            application_version: data.application_version,
            country_code: data.country_code,
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/delete`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${data.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    receiveCopyData(data: any): Promise<any> {
        const body: { [key: string]: string } = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            resident_of: data.residentOf,
            sid: data.sid,
            fsa: data.fsa,
            locale: data.locale,
            platform: 'flipp_web',
            application_version: data.application_version,
            country_code: data.country_code,
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/request_data`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${data.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    notSellData(data: any): Promise<any> {
        const body: { [key: string]: string } = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            resident_of: data.residentOf,
            sid: data.sid,
            fsa: data.fsa,
            locale: data.locale,
            platform: 'flipp_web',
            application_version: data.application_version,
            country_code: data.country_code,
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/do_not_sell`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${data.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    getAppRating(): Promise<RatingObject> {
        const { locale } = this.locationService
        return fetchJSON<RatingObject>(
            `${this.config.FLIPP_COM_APIS}/app/ratings/${locale}`
        )
    }
}
